import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiXCircle, FiEye, FiFileText, FiPrinter, FiDollarSign, FiLink, FiPackage
} from 'react-icons/fi';
import styled from 'styled-components';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import LinkedDocNumbers, { parentLinks } from '../../components/LinkedDocNumbers';
import { getItemsCached, getCustomersCached, getAccountsCached, getEmployeesCached, getJobsCached, getLocationsCached, getAreasRegionsCached } from '../../utils/masterDataStore';
import { confirmZeroPriceLines } from '../../utils/zeroPriceLines';
import { getCustomerBalance } from '../../utils/liveBalanceHelper';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import LetterheadPicker from '../../components/LetterheadPicker';
import { INVOICE_LETTERHEAD_MODULE, listInvoiceLetterheads, letterheadForChoice } from '../../utils/invoiceLetterheads';
import {
  createTransaction, updateTransaction, deleteTransaction,
  getTransactions, getTransaction, updateTransactionStatus
} from '../../utils/transactionHelper';
import { deleteSubTransactions } from '../../utils/subTransactionHelper';
import { deleteStockFlow, getAvailableBatches, getAvailableSerials, getItemStockCached, invalidateItemStock } from '../../utils/stockFlowHelper';
import LineUnitPicker from '../../components/LineUnitPicker';
import TwoUnitQtyBox, { TwoUnitNote } from '../../components/TwoUnitQtyBox';
import { seedLineUnitFromItem, applyUnitChangeToLine, twoUnitSwitchQty } from '../../utils/unitConversion';
import { loadIfEnabled as loadCustomerPricing, EMPTY_PRICING, filterItemsByPricing, getOverridePrice } from '../../utils/customerPricingHelper';
import { subCategoryLineDiscount } from '../../utils/customerSubCategoryDiscount';
import { recomputeSizeLine } from '../../utils/sizeHelper';
import PageLayout from '../../components/PageLayout';
import SendWhatsAppButton from '../../components/SendWhatsAppButton';
import CustomerSelect from '../../components/CustomerSelect';
import ItemSelect from '../../components/ItemSelect';
import SerialSelectPopover from '../../components/SerialSelectPopover';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import AttachmentPanel from '../../components/AttachmentPanel';
import {
  checkStockAvailability, checkCustomerCreditLimit, isFutureDate,
  isLargeTransaction, LARGE_TRANSACTION_THRESHOLD, writeAuditLog, toBaseQty
} from '../../utils/validationHelpers';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, BalanceRow, DiscountInput,
  PaymentSection, LinkedBadgeContainer, LinkedDocsSection, SortableTh, SortContext
} from '../../components/SharedStyles';
import SizeLineRow from '../../components/SizeLineRow';
import { startSaveTimer } from '../../utils/saveTimer';
import { prewarmSave } from '../../utils/transactionHelper';
import { doc, getDoc, getDocs, updateDoc, collection, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { inActiveBranch } from '../../utils/branchLink';
import { carryEmployeeJob, customerPOOf, joinCustomerPOs } from '../../utils/linkCarry';

const TRAN_TYPE_CODE = 'NSI';

// Footer summary spec — aggregated server-side in paged mode (~few reads),
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Posted', 'Paid'],
  outstandingStatuses: ['Posted', 'Partial'],
  outstandingField: 'balanceAmount'
};

const EMPTY_LINE = {
  itemId: '',
  accountId: '',
  description: '',
  quantity: 1,
  unitOfMeasure: '',
  unitCode: '',
  unitPrice: 0,
  unitCost: 0, // Cost price for COGS calculation
  priceType: 'Retail',
  discountPercent: 0,
  discountAmount: 0,
  taxable: false,
  taxRate: 0,
  taxAmount: 0,
  mrp: 0,
  lineTotal: 0,
  totalCost: 0, // quantity * unitCost for COGS entries
  trackingType: 'None',
  batchNumber: '',
  serialNumbers: [],
  sourceDocId: '',
  sourceDocNumber: '',
  sourceDocDate: '',
  sourceLineNumber: 0
};

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.info.main,
  Partial: '#9C27B0',
  Paid: colors.success.main,
  Cancelled: colors.error.main,
};

// ── Sale Order delivery progress (FLW-03) ───────────────────────────────────
// An SO is delivered by its active Delivery Challans (not Draft / Cancelled) and
// by posted invoices made straight from it. Each invoice line made from an SO
// keeps the SO line it came from (soLineNumber = that line's number on the SO);
// older lines without it fall back to the same item (or description).
const DC_ACTIVE = (s) => !!s && s !== 'Draft' && s !== 'Cancelled';
const INVOICE_LIVE = ['Posted', 'Partial', 'Paid'];
// 'Delivered' is an old, invalid SO status — it is corrected on the next recalc.
const SO_PROGRESS_STATUSES = ['Approved', 'Partially Delivered', 'Delivered', 'Completed'];
const r6 = (n) => Math.round((Number(n) || 0) * 1e6) / 1e6;
const soLineNo = (sl, idx) => Number(sl?.lineNumber) || (idx + 1);

const findSOLineIndex = (soLines, delivered, ref, itemId, desc, qty, removing) => {
  const n = Number(ref) || 0;
  if (n > 0) {
    const i = soLines.findIndex((sl, idx) => soLineNo(sl, idx) === n);
    if (i >= 0 && (!itemId || !soLines[i].itemId || soLines[i].itemId === itemId)) return i;
  }
  const same = [];
  soLines.forEach((sl, idx) => {
    if (itemId ? sl.itemId === itemId : (!!desc && !sl.itemId && sl.description === desc)) same.push(idx);
  });
  const fit = same.find(idx => (removing
    ? (delivered[idx] || 0) >= qty
    : (Number(soLines[idx].quantity) || 0) - (delivered[idx] || 0) > 0));
  return fit !== undefined ? fit : (same.length ? same[0] : -1);
};

const countsOnSO = (d) => (d.tranTypeCode === 'DC'
  ? DC_ACTIVE(d.status)
  : (['SI', 'NSI'].includes(d.tranTypeCode) && INVOICE_LIVE.includes(d.status)));

// Delivered qty per SO line from the SO's child documents (skipId = leave one out).
// Lines that carry their SO line number are applied first, older lines without
// it then fill whatever is still open on a line of the same item.
export const soDeliveredFromChildren = (soLines, children, skipId) => {
  const delivered = (soLines || []).map(() => 0);
  const live = (children || []).filter(c => c && c.id !== skipId && countsOnSO(c));
  const add = (l) => {
    const qty = Number(l.quantity) || 0;
    if (!(qty > 0)) return;
    const i = findSOLineIndex(soLines, delivered, l.soLineNumber, l.itemId || '', l.description || '', qty, false);
    if (i >= 0) delivered[i] = r6(delivered[i] + qty);
  };
  live.forEach(c => (c.lines || []).forEach(l => { if (Number(l.soLineNumber) > 0) add(l); }));
  live.forEach(c => (c.lines || []).forEach(l => { if (!(Number(l.soLineNumber) > 0)) add(l); }));
  return delivered;
};

// SO line number each document line belongs to (tags older lines saved without
// it). `progress` (from loadSOProgress) = what OTHER documents already delivered,
// so an old line goes to the SO line that still has room for it.
export const tagSOLineNumbers = (soLines, docLines, progress = null) => {
  const used = (soLines || []).map((sl, i) => {
    const p = progress && progress[soLineNo(sl, i)];
    return p ? (Number(p.delivered) || 0) : 0;
  });
  return (docLines || []).map(l => {
    const qty = Number(l.quantity) || 0;
    const i = findSOLineIndex(soLines, used, l.soLineNumber, l.itemId || '', l.description || '', qty, false);
    if (i < 0) return 0;
    used[i] = r6(used[i] + qty);
    return soLineNo(soLines[i], i);
  });
};

// Approved (nothing delivered) / Partially Delivered / Completed (all delivered).
// A Draft or Cancelled SO keeps its status.
export const soStatusFromLines = (lines, current) => {
  if (!SO_PROGRESS_STATUSES.includes(current)) return current;
  const all = lines.length > 0 && lines.every(l => (Number(l.deliveredQty) || 0) >= (Number(l.quantity) || 0));
  const some = lines.some(l => (Number(l.deliveredQty) || 0) > 0);
  return all ? 'Completed' : some ? 'Partially Delivered' : 'Approved';
};

const listSOChildren = async (companyId, soId) => {
  const snap = await getDocs(query(collection(db, 'companies', companyId, 'transactions'), where('parentId', '==', soId)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Per SO line (keyed by its line number): ordered, delivered by OTHER documents,
// and a label. Always counted from the SO's linked documents (one query), so a
// list of orders loaded earlier on this screen can never make it stale.
export const loadSOProgress = async (companyId, so, self) => {
  const soLines = so.lines || [];
  const delivered = soDeliveredFromChildren(soLines, await listSOChildren(companyId, so.id), self ? self.id : null);
  const progress = {};
  soLines.forEach((l, i) => {
    progress[soLineNo(l, i)] = { ordered: Number(l.quantity) || 0, delivered: delivered[i], label: l.description || l.itemName || `Line ${i + 1}` };
  });
  return progress;
};

// Message when a line asks for more than is left on the SO ('' when fine).
export const soOverDeliveryMessage = (lines, progress, soNumber) => {
  if (!progress) return '';
  const want = {};
  (lines || []).forEach(l => {
    const n = Number(l.soLineNumber) || 0;
    if (n > 0 && progress[n]) want[n] = r6((want[n] || 0) + (Number(l.quantity) || 0));
  });
  const over = Object.keys(want).filter(n => want[n] > r6(progress[n].ordered - progress[n].delivered) + 1e-6);
  if (over.length === 0) return '';
  return `Quantity is more than what is left on Sale Order ${soNumber || ''}:\n\n` + over.map(n => {
    const p = progress[n];
    return `• ${p.label}: ordered ${p.ordered}, already delivered ${p.delivered}, remaining ${Math.max(0, r6(p.ordered - p.delivered))} — you entered ${want[n]}`;
  }).join('\n') + '\n\nPlease reduce the quantity to the remaining amount.';
};

// Recount the SO's per-line delivered / pending quantities from all its linked
// documents (active challans + posted direct invoices) and set a valid status.
// Writes only lines / status / updatedAt / updatedBy (the SO progress fields an
// invoice user may change). Called after the invoice itself was saved / deleted.
export const recalcSOFromChildren = async (companyId, soId, userId) => {
  const ref = doc(db, 'companies', companyId, 'transactions', soId);
  const snap = await getDoc(ref);
  if (!snap.exists() || snap.data().tranTypeCode !== 'SO') return null;
  const so = snap.data();
  const soLines = Array.isArray(so.lines) ? so.lines : [];
  const delivered = soDeliveredFromChildren(soLines, await listSOChildren(companyId, soId), null);
  const lines = soLines.map((l, i) => ({
    ...l, deliveredQty: delivered[i], pendingQty: Math.max(0, r6((Number(l.quantity) || 0) - delivered[i]))
  }));
  const upd = { lines, updatedAt: new Date().toISOString(), ...(userId ? { updatedBy: userId } : {}) };
  const status = soStatusFromLines(lines, so.status);
  if (status !== so.status) upd.status = status;
  await updateDoc(ref, upd);
  return { ...so, ...upd };
};

// Save / edit / cancel / delete of an invoice: recount the SO(s) it was or is
// linked to.
export const syncInvoiceSOProgress = async (companyId, oldInv, newInv, userId) => {
  const ids = [];
  [oldInv, newInv].forEach(inv => {
    if (inv && inv.parentTranType === 'SO' && inv.parentId && !ids.includes(inv.parentId)) ids.push(inv.parentId);
  });
  for (const id of ids) {
    // eslint-disable-next-line no-await-in-loop
    await recalcSOFromChildren(companyId, id, userId);
  }
};

// LinkedBadge component for document linking display
// Shows the linked documents' NUMBERS (not a count). `known` = numbers the
// document already carries (no lookup); the rest are looked up once per device.
const LinkedBadge = ({ label, ids, known, max = 3, icon: Icon = FiLink }) => {
  const list = (ids || []).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <LinkedBadgeContainer>
      <Icon size={12} />
      <span>{label}:</span>
      <LinkedDocNumbers ids={list} known={known} max={max} showIcon={false} color="inherit" style={{ fontSize: 'inherit' }} />
    </LinkedBadgeContainer>
  );
};

const SaleNonTaxInvoice = () => {
  const location = useLocation();
  const { activeCompany, activeFinancialYear, currentUser, activeBranch, isModuleEnabled } = useAuth();
  // Speed: prepare the first save in the background as soon as this screen opens.
  useEffect(() => {
    if (!activeCompany?.id || !activeFinancialYear?.id) return;
    prewarmSave(activeCompany.id, {
      tranTypeCode: TRAN_TYPE_CODE,
      branchId: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.id : null,
      financialYearId: activeFinancialYear.id,
    });
  }, [activeCompany?.id, activeFinancialYear?.id, activeBranch?.id]);
  const mrpEnabled = isModuleEnabled && isModuleEnabled('mrp_pricing');
  const priceClassEnabled = isModuleEnabled && isModuleEnabled('customer_class');
  const { canEdit, canDelete, canCancel, canChange } = usePagePermissions('transactions', 'nonTaxSaleInvoice');
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [printConfig, setPrintConfig] = useState(null);
  const letterheadChoiceOn = !!(isModuleEnabled && isModuleEnabled(INVOICE_LETTERHEAD_MODULE));
  const [letterheadPickFor, setLetterheadPickFor] = useState(null);
  useEffect(() => { if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [activeCompany]);

  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: invoices,
    loading,
    refresh: loadInvoices,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingInvoice, setEditingInvoice] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docNumber: '',
    docDate: new Date().toISOString().split('T')[0],
    partyId: '',
    partyName: '',
    // Optional per-invoice walk-in details — stored on THIS invoice only, never
    // written to the customer master or the ledger. Shown only when the Walk-in
    // customer is selected.
    walkInName: '',
    walkInPhone: '',
    parentId: '', // DC or SO link
    parentDocNumber: '',
    parentTranType: '', // 'DC' or 'SO'
    parentIds: [], // multi-DC
    customerPO: '', // customer's own PO number (optional)
    dueDate: '',
    paymentTerms: '',
    currency: 'PKR',
    discountAmount: 0,
    whtRate: 0,
    whtAmount: 0,
    transportationCharges: 0,
    otherCharges: 0,
    notes: '',
    status: 'Draft',
    employeeId: '', employeeName: '', jobId: '', jobName: '',
    locationId: '', locationName: '',
    paymentAmount: 0, paymentAccountId: '', paymentAccountName: '',
    existingPaidAmount: 0,
    // Optional credit-account override (blank = default Sales – General).
    creditAccountId: '', creditAccountCode: '', creditAccountName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);

  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [customerPricing, setCustomerPricing] = useState(EMPTY_PRICING);
  const [deliveredDCs, setDeliveredDCs] = useState([]);
  const [approvedSOs, setApprovedSOs] = useState([]);
  // FLW-03: per SO line { ordered, delivered (by other documents), label } for the
  // Sale Order this invoice is made from (null when not linked / not loaded).
  const [soProgress, setSoProgress] = useState(null);
  const soPickRef = useRef('');
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  // All Level-5 revenue-type accounts for the optional credit-account override.
  const [allAccounts, setAllAccounts] = useState([]);
  const [availableBatches, setAvailableBatches] = useState({});  // keyed by itemId
  const [availableSerials, setAvailableSerials] = useState({});  // keyed by itemId
  const [stockBalances, setStockBalances] = useState({});

  // Invoice list now comes from useDocListPagination above (loadInvoices = its
  // refresh). The old full-history getTransactions load lived here.

  // Tier 1 (2026-07-27, Firebase cost): customers/items/accounts come from the
  // device-local master cache (getMaster* → IndexedDB, 0 reads per open with a
  // throttled background change-check) instead of a full getDocs on every open.
  // Each screen keeps its own client-side filter/sort on the returned rows.
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getCustomersCached(activeCompany.id);
      setCustomers([...rows].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '')));
    } catch (error) { console.error('Error loading customers:', error); }
  }, [activeCompany]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) { console.error('Error loading items:', error); }
  }, [activeCompany]);

  // ── D3 (2026-07-23, Firebase cost): the three loaders below feed ONLY the
  // form's "Link to DC / Link to SO" dropdowns — nothing on the list view uses
  // them. They used to run on every page open AND after every save, each
  // scanning full document history (all DCs + all SOs + every SI *and* NSI
  // ever). They now load ONCE, lazily, the first time a form is actually
  // opened. List browsing and saving cost nothing.
  const formDataLoadedRef = useRef(false);

  const loadDeliveredDCs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Firebase cost: load ONLY still-open (not-yet-invoiced) DCs. Invoicing a DC
      // flips its status to 'Invoiced' (see handleSave), so Delivered/Dispatched
      // ARE exactly the linkable set. This drops the 'Invoiced'-DC read AND removes
      // the need to scan the entire SI/NSI history to detect already-invoiced ones.
      const [delivered, dispatched] = await Promise.all([
        getTransactions(activeCompany.id, { tranTypeCode: 'DC', status: 'Delivered' }),
        getTransactions(activeCompany.id, { tranTypeCode: 'DC', status: 'Dispatched' })
      ]);
      const byId = {};
      [...delivered, ...dispatched].forEach(d => { byId[d.id] = d; });
      setDeliveredDCs(Object.values(byId));
    } catch (error) { console.error('Error loading delivery challans:', error); }
  }, [activeCompany]);

  const loadApprovedSOs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Load approved SOs from unified transactions collection
      const approvedSOsList = await getTransactions(activeCompany.id, {
        tranTypeCode: 'SO', status: 'Approved'
      });
      const partialSOsList = await getTransactions(activeCompany.id, {
        tranTypeCode: 'SO', status: 'Partially Delivered'
      });
      setApprovedSOs([...approvedSOsList, ...partialSOsList]);
    } catch (error) { console.error('Error loading sale orders:', error); }
  }, [activeCompany]);

  // Loads the form-only dropdown data once per page session (see D3 note above).
  const ensureFormData = useCallback(async () => {
    if (formDataLoadedRef.current || !activeCompany?.id) return;
    formDataLoadedRef.current = true;
    try {
      await Promise.all([loadDeliveredDCs(), loadApprovedSOs()]);
    } catch (error) {
      formDataLoadedRef.current = false; // let a later form open retry
      console.error('Error loading form link data:', error);
    }
  }, [activeCompany, loadDeliveredDCs, loadApprovedSOs]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  const loadPaymentAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const allAccts = await getAccountsCached(activeCompany.id);
      const allLvl5 = allAccts.filter(a => a.level === 5 || a.isPostable);
      setPaymentAccounts(allLvl5.filter(a => a.code?.startsWith('11101') || a.code?.startsWith('11102')));
      // Credit-account override picker: revenue (4xxxx) only. Exclude
      // party-linked accounts so users can't accidentally credit a customer
      // master account from here. (Same list as the Sale Invoice.)
      setAllAccounts(allLvl5.filter(a => {
        if (a.linkedEntityType === 'customer' || a.linkedEntityType === 'vendor') return false;
        return a.code?.startsWith('4');
      }).sort((a, b) => (a.code || '').localeCompare(b.code || '')));
    } catch (error) { console.error('Error loading payment accounts:', error); }
  }, [activeCompany]);

  // Firebase cost: stock is fetched ONE ITEM AT A TIME, on demand. The old
  // mount-time load scanned the company's ENTIRE stockFlow history just to draw
  // badges the user may never look at. Now the dropdown shows a "stock?" chip
  // and this runs only when the user taps it (or when the availability check
  // needs an item it doesn't already have).
  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch]);

  // NOTE: the invoice list itself loads inside useDocListPagination — not here.
  // D3: DC/SO/cross-invoice loaders are NOT here anymore — they load lazily on
  // first form open (ensureFormData), since only the form uses them.
  useEffect(() => {
    loadCustomers(); loadItems();
    loadEmployees(); loadJobs(); loadLocations(); loadPaymentAccounts();
  }, [loadCustomers, loadItems, loadEmployees, loadJobs, loadLocations, loadPaymentAccounts]);

  // Reload batches/serials whenever the active inventory location changes.
  // Without this, picking an item before choosing a location (or switching
  // location after) shows company-wide serials instead of stock at this location.
  useEffect(() => {
    const locId = formData.locationId;
    if (!locId || !activeCompany?.id) return;
    const brId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null;
    const batchIds = new Set();
    const serialIds = new Set();
    lineItems.forEach(l => {
      if (!l.itemId) return;
      if (l.trackingType === 'Batch') batchIds.add(l.itemId);
      if (l.trackingType === 'Serial') serialIds.add(l.itemId);
    });
    batchIds.forEach(iid => {
      getAvailableBatches(activeCompany.id, iid, locId, brId).then(b => setAvailableBatches(prev => ({ ...prev, [iid]: b })));
    });
    serialIds.forEach(iid => {
      getAvailableSerials(activeCompany.id, iid, locId, brId).then(s => setAvailableSerials(prev => ({ ...prev, [iid]: s })));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.locationId, activeCompany?.id, activeBranch?.id]);

  // Handle navigation from reports (e.g., Journal Report clicking on document #).
  // With pagination the target may not be in the loaded page — ensureDoc
  // fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(transaction => {
      if (transaction) {
        setViewingInvoice(transaction);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  // Filter
  const filteredInvoices = useMemo(() => {
    let filtered = invoices;
    if (statusFilter !== 'All') filtered = filtered.filter(inv => inv.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(inv =>
        inv.docNumber?.toLowerCase().includes(q) ||
        inv.partyName?.toLowerCase().includes(q) ||
        inv.parentDocNumber?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [invoices, statusFilter, searchQuery, sort]);

  // Calculate line. MRP is manually entered per line and contributes to lineTotal.
  const calculateLine = (line) => {
    const amount = line.quantity * line.unitPrice;
    const discAmt = amount * (line.discountPercent / 100);
    const net = amount - discAmt;
    const totalCost = (line.quantity || 0) * (line.unitCost || 0);
    const mrp = Number(line.mrp) || 0;
    return {
      ...line,
      taxable: false,
      taxRate: 0,
      discountAmount: Math.round(discAmt * 100) / 100,
      taxAmount: 0,
      lineTotal: Math.round((net + mrp) * 100) / 100,
      totalCost: Math.round(totalCost * 100) / 100
    };
  };

  // Header totals (non-tax: taxAmount always 0)
  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    const subtotal = lines.reduce((s, l) => s + (l.quantity * l.unitPrice - l.discountAmount), 0);
    const mrpTotal = lines.reduce((s, l) => s + (Number(l.mrp) || 0), 0);
    const disc = Number(formData.discountAmount) || 0;
    const transportationCharges = Number(formData.transportationCharges) || 0;
    const otherCharges = Number(formData.otherCharges) || 0;
    const beforeWht = subtotal - disc + mrpTotal;
    const whtRate = Number(formData.whtRate) || 0;
    const wht = Math.round(subtotal * (whtRate / 100) * 100) / 100;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmount: 0,
      mrpTotal: Math.round(mrpTotal * 100) / 100,
      headerDiscount: disc,
      whtAmount: wht,
      transportationCharges: Math.round(transportationCharges * 100) / 100,
      otherCharges: Math.round(otherCharges * 100) / 100,
      totalAmount: Math.round((beforeWht - wht + transportationCharges + otherCharges) * 100) / 100
    };
  }, [lineItems, formData.discountAmount, formData.whtRate, formData.transportationCharges, formData.otherCharges]);

  // Form handlers
  const addDays = (isoDate, n) => {
    const days = Number(n);
    if (!isoDate || !days || days <= 0) return '';
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };

  const handleHeaderChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'docDate' && prev.partyId) {
        const party = customers.find(c => c.id === prev.partyId);
        const days = Number(party?.creditDays || party?.paymentTerms) || 0;
        if (days > 0) next.dueDate = addDays(value, days);
      }
      return next;
    });
  };

  const handleCustomerChange = (customerId, party) => {
    // Unified-mode aware: party may be a customer OR a vendor (when unified_party_picker is on).
    // Preserve the real partyType so accounting routes to the party's own account.
    const resolved = party || customers.find(c => c.id === customerId);
    const isVendor = resolved?._partyType === 'vendor';
    const days = Number(resolved?.creditDays || resolved?.paymentTerms) || 0;
    // Auto-fill the salesperson (employee) from the customer's saved default (customers only).
    const spPatch = (!isVendor && resolved?.salesPersonId)
      ? { employeeId: resolved.salesPersonId, employeeName: resolved.salesPersonName || '' }
      : {};
    setFormData(prev => ({
      ...prev, partyId: customerId,
      partyName: resolved?.companyName || resolved?.vendorName || '',
      partyType: isVendor ? 'vendor' : 'customer',
      // Fresh party → clear any previously-typed walk-in name/phone.
      walkInName: '', walkInPhone: '',
      paymentTerms: days ? `${days} days` : prev.paymentTerms,
      dueDate: days ? addDays(prev.docDate, days) : prev.dueDate,
      parentId: '', parentDocNumber: '', parentTranType: '',
      ...spPatch
    }));
    setLineItems([{ ...EMPTY_LINE }]);
    setSoProgress(null);
    soPickRef.current = '';
    if (!isVendor && customerId) {
      loadCustomerPricing(activeCompany?.id, customerId, isModuleEnabled)
        .then(setCustomerPricing)
        .catch(() => setCustomerPricing(EMPTY_PRICING));
    } else {
      setCustomerPricing(EMPTY_PRICING);
    }
  };

  // Customer PO from the linked challan(s) (2026-11-02). A challan saved before
  // it had its own Customer PO takes its Sale Order's — one small read, only
  // when linking, cached per Sale Order. Several challans → each PO once.
  const soPOCacheRef = useRef({});
  const fillCustomerPOFromDCs = async (ids) => {
    const pos = [];
    for (const id of ids) {
      const dc = deliveredDCs.find(d => d.id === id);
      if (!dc) continue;
      let po = customerPOOf(dc);
      if (!po && dc.parentId && activeCompany?.id) {
        if (!(dc.parentId in soPOCacheRef.current)) {
          try {
            const s = await getDoc(doc(db, 'companies', activeCompany.id, 'transactions', dc.parentId));
            soPOCacheRef.current[dc.parentId] = s.exists() ? customerPOOf(s.data()) : '';
          } catch (_) { soPOCacheRef.current[dc.parentId] = ''; }
        }
        po = soPOCacheRef.current[dc.parentId];
      }
      pos.push(po);
    }
    const joined = joinCustomerPOs(pos);
    if (!joined) return;
    setFormData(prev => (JSON.stringify(prev.parentIds || []) === JSON.stringify(ids) ? { ...prev, customerPO: joined } : prev));
  };

  const addDC = (dcId) => {
    if (!dcId) return;
    const dc = deliveredDCs.find(d => d.id === dcId);
    if (!dc) return;
    if (formData.parentIds.includes(dcId)) return;
    if (formData.parentIds.length > 0 && formData.partyId && dc.partyId !== formData.partyId) {
      alert('All linked DCs must belong to the same customer.');
      return;
    }
    const isFirst = formData.parentIds.length === 0;
    const newLines = (dc.lines || []).map((l, idx) => {
      const item = items.find(i => i.id === l.itemId);
      const trackingType = l.trackingType || item?.trackingType || 'None';
      const dcLocId = dc.locationId || dc.fromLocationId || formData.locationId || null;
      const brId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null;
      if (l.itemId && trackingType === 'Batch') {
        getAvailableBatches(activeCompany.id, l.itemId, dcLocId, brId).then(batches => {
          setAvailableBatches(prev => ({ ...prev, [l.itemId]: batches }));
        });
      } else if (l.itemId && trackingType === 'Serial') {
        getAvailableSerials(activeCompany.id, l.itemId, dcLocId, brId).then(serials => {
          setAvailableSerials(prev => ({ ...prev, [l.itemId]: serials }));
        });
      }
      return {
        ...EMPTY_LINE,
        itemId: l.itemId || '',
        itemCode: l.itemCode || item?.itemCode || '',
        itemName: l.itemName || item?.itemName || '',
        description: l.description || '',
        quantity: l.quantity || 0,
        unitPrice: l.unitPrice || 0,
        unitCost: Number(l.unitCost) || Number(item?.averageCost) || item?.purchasePrice || 0, // INV-04: the cost the challan took out of stock (its stock row), so profit matches stock
        unitOfMeasure: l.unitOfMeasure || item?.unitOfMeasure || item?.unit || 'Pcs',
        // Carry the DC line's discount (2026-10-28 — was dropped here; the Sale
        // Invoice already carried it). The invoice works the amount out from the %.
        discountPercent: Number(l.discountPercent) || 0,
        discountAmount: Number(l.discountAmount) || 0,
        taxable: false,
        taxRate: 0,
        trackingType,
        batchNumber: l.batchNumber || '',
        serialNumbers: l.serialNumbers || [],
        sourceDocId: dc.id,
        sourceDocNumber: dc.docNumber || '',
        sourceDocDate: dc.docDate || '',
        sourceLineNumber: l.lineNumber || (idx + 1)
      };
    });
    setFormData(prev => ({
      ...prev,
      parentIds: [...prev.parentIds, dcId],
      parentTranType: 'DC',
      parentId: isFirst ? dcId : prev.parentId,
      parentDocNumber: isFirst ? (dc.docNumber || '') : prev.parentDocNumber,
      partyId: isFirst ? dc.partyId : prev.partyId,
      partyName: isFirst ? dc.partyName : prev.partyName,
      locationId: isFirst ? (dc.locationId || dc.fromLocationId || '') : prev.locationId,
      locationName: isFirst ? (dc.locationName || dc.fromLocationName || '') : prev.locationName,
      ...(isFirst ? carryEmployeeJob(dc) : {})
    }));
    setLineItems(prev => {
      const hasOnlyEmpty = prev.length === 1 && !prev[0].itemId && !prev[0].description && !prev[0].sourceDocId;
      return hasOnlyEmpty ? newLines : [...prev, ...newLines];
    });

    fillCustomerPOFromDCs([...formData.parentIds, dcId]);
  };

  const removeDC = (dcId) => {
    const remaining = formData.parentIds.filter(id => id !== dcId);
    const remainingLines = lineItems.filter(l => l.sourceDocId !== dcId);
    if (remaining.length === 0) {
      setFormData(prev => ({
        ...prev, parentIds: [], parentId: '', parentDocNumber: '', parentTranType: ''
      }));
      setLineItems([{ ...EMPTY_LINE }]);
      return;
    }
    const firstId = remaining[0];
    const firstDC = deliveredDCs.find(d => d.id === firstId);
    setFormData(prev => ({
      ...prev, parentIds: remaining, parentId: firstId, parentDocNumber: firstDC?.docNumber || ''
    }));
    setLineItems(remainingLines.length ? remainingLines : [{ ...EMPTY_LINE }]);

    fillCustomerPOFromDCs(remaining);
  };

  const handleSOChange = async (parentId) => {
    const so = approvedSOs.find(s => s.id === parentId);
    soPickRef.current = so ? parentId : '';
    if (!so) {
      setFormData(prev => ({ ...prev, parentId: '', parentDocNumber: '', parentTranType: '' }));
      setSoProgress(null);
      return;
    }
    setFormData(prev => ({
      ...prev, parentId, parentDocNumber: so.docNumber || '',
      parentTranType: 'SO',
      partyId: so.partyId, partyName: so.partyName,
      ...(customerPOOf(so) ? { customerPO: customerPOOf(so) } : {}),
      ...carryEmployeeJob(so)
    }));
    // FLW-03: what each SO line already has delivered (by other documents).
    let progress = null;
    try {
      progress = await loadSOProgress(activeCompany.id, so,
        editingInvoice && editingInvoice.parentId === so.id ? editingInvoice : null);
    } catch (e) { console.error('Error loading sale order progress:', e); }
    if (soPickRef.current !== parentId) return; // another order was picked meanwhile
    setSoProgress(progress);
    const remainingOf = (n) => (progress && progress[n]) ? Math.max(0, r6(progress[n].ordered - progress[n].delivered)) : null;
    // Auto-fill lines from SO (with cost lookup). Fully delivered lines are not
    // offered; the rest pre-fill with what is left to deliver.
    const soLines = (so.lines || []).map((l, idx) => ({ ...l, _soLineNo: soLineNo(l, idx) }))
      .filter(l => remainingOf(l._soLineNo) === null || remainingOf(l._soLineNo) > 0);
    if ((so.lines || []).length > 0 && soLines.length === 0) {
      alert(`Every line of Sale Order ${so.docNumber || ''} is already fully delivered.`);
      soPickRef.current = '';
      setFormData(prev => ({ ...prev, parentId: '', parentDocNumber: '', parentTranType: '' }));
      setSoProgress(null);
      setLineItems([{ ...EMPTY_LINE }]);
      return;
    }
    if (soLines.length > 0) {
      setLineItems(soLines.map(l => {
        const item = items.find(i => i.id === l.itemId);
        const trackingType = l.trackingType || item?.trackingType || 'None';
        // Load available batches/serials for tracked items, scoped to location AND branch.
        const soLocId = so.locationId || formData.locationId || null;
        const brId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null;
        if (l.itemId && trackingType === 'Batch') {
          getAvailableBatches(activeCompany.id, l.itemId, soLocId, brId).then(batches => {
            setAvailableBatches(prev => ({ ...prev, [l.itemId]: batches }));
          });
        } else if (l.itemId && trackingType === 'Serial') {
          getAvailableSerials(activeCompany.id, l.itemId, soLocId, brId).then(serials => {
            setAvailableSerials(prev => ({ ...prev, [l.itemId]: serials }));
          });
        }
        return {
          ...EMPTY_LINE,
          itemId: l.itemId || '',
          itemCode: l.itemCode || item?.itemCode || '',
          itemName: l.itemName || item?.itemName || '',
          description: l.description || '',
          soLineNumber: l._soLineNo,
          quantity: remainingOf(l._soLineNo) === null ? (l.quantity || 0) : remainingOf(l._soLineNo),
          unitPrice: l.unitPrice || 0,
          unitCost: Number(item?.averageCost) || l.unitCost || item?.purchasePrice || 0, // INV-04: weighted average first
          unitOfMeasure: l.unitOfMeasure || item?.unitOfMeasure || item?.unit || 'Pcs',
          // Carry the SO line's discount % so SO → Invoice keeps the same amounts.
          discountPercent: Number(l.discountPercent) || 0,
          taxable: false,
          taxRate: 0,
          trackingType,
          batchNumber: l.batchNumber || '',
          serialNumbers: l.serialNumbers || []
        };
      }));
    }
  };

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Price type change - auto-fill unit price from item
      if (field === 'priceType' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          if (value === 'Retail') updated[index].unitPrice = item.salePrice || 0;
          else if (value === 'Wholesale') updated[index].unitPrice = item.wholesalePrice || 0;
          else if (value === 'Dealer') updated[index].unitPrice = item.dealerPrice || 0;
          const ovr = getOverridePrice(customerPricing, updated[index].itemId);
          if (ovr !== null) updated[index].unitPrice = ovr;
        }
      }

      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'salePrice', costFromAverage: true });
          updated[index] = { ...updated[index], ...patch };
          const ovr = getOverridePrice(customerPricing, updated[index].itemId);
          if (ovr !== null) updated[index].unitPrice = ovr;
          // Show Two Units: switching "Rate per" between the item's two units keeps the same physical qty
          // (not on lines linked to another document — their qty is tracked against it).
          const twoQ = (formData.parentId || prev[index].sourceDocId || prev[index].poLineId) ? null
            : twoUnitSwitchQty(item, prev[index].unitCode || prev[index].unitOfMeasure, value, prev[index].quantity);
          if (twoQ !== null) updated[index].quantity = twoQ;
        }
      }

      // Size-mode line edits: recompute Total SQft when L / H / Thickness / Pieces / sizeUnit change.
      if (updated[index].hasSize && ['length', 'height', 'thickness', 'pieces', 'sizeUnit'].includes(field)) {
        updated[index] = recomputeSizeLine(updated[index]);
      }

      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].itemCode = item.itemCode || '';
          updated[index].itemName = item.itemName || '';
          updated[index].description = item.itemName || '';
          // Customer Class Pricing: default the price tier from the customer's class.
          const cls = (priceClassEnabled && customers.find(c => c.id === formData.partyId)?.customerClass) || null;
          updated[index].priceType = cls || 'Retail';
          updated[index].taxRate = 0;
          updated[index].taxable = false;
          const seeded = seedLineUnitFromItem(item, { priceField: 'salePrice', defaultUnitField: 'defaultSaleUnit', costFromAverage: true });
          updated[index] = { ...updated[index], ...seeded };
          if (cls === 'Wholesale') updated[index].unitPrice = item.wholesalePrice || updated[index].unitPrice;
          else if (cls === 'Dealer') updated[index].unitPrice = item.dealerPrice || updated[index].unitPrice;
          updated[index].trackingType = item.trackingType || 'None';
          updated[index].batchNumber = '';
          updated[index].serialNumbers = [];
          const ovr = getOverridePrice(customerPricing, value);
          if (ovr !== null) updated[index].unitPrice = ovr;
          // Customer Discount by Sub-Category (module): the item's sub-category % for this customer.
          const scd = subCategoryLineDiscount(customers.find(c => c.id === formData.partyId), item, isModuleEnabled);
          if (scd !== undefined) updated[index].discountPercent = scd;

          // Size mode: prime with item defaults and force unit to SQft.
          if (item.hasSize) {
            updated[index].hasSize = true;
            updated[index].sizeUnit = item.sizeUnit === 'cm' ? 'cm' : 'inches';
            updated[index].length = Number(item.defaultLength) || 0;
            updated[index].height = Number(item.defaultHeight) || 0;
            updated[index].thickness = Number(item.defaultThickness) || 0;
            updated[index].pieces = updated[index].pieces || 1;
            updated[index].unitOfMeasure = 'SQft';
            updated[index].unitCode = 'SQft';
            updated[index] = recomputeSizeLine(updated[index]);
          } else {
            updated[index].hasSize = false;
          }
          // Load available batches/serials for this item, scoped to location AND branch.
          const curLocId = formData.locationId || null;
          const brId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null;
          if (item.trackingType === 'Batch') {
            getAvailableBatches(activeCompany.id, value, curLocId, brId).then(batches => {
              setAvailableBatches(prev => ({ ...prev, [value]: batches }));
              // FIFO auto-fill: select oldest batch
              if (batches.length > 0) {
                setLineItems(prev => {
                  const u = [...prev];
                  if (u[index] && u[index].itemId === value) {
                    u[index] = { ...u[index], batchNumber: batches[0].batchNumber };
                  }
                  return u;
                });
              }
            });
          } else if (item.trackingType === 'Serial') {
            getAvailableSerials(activeCompany.id, value, curLocId, brId).then(serials => {
              setAvailableSerials(prev => ({ ...prev, [value]: serials }));
            });
          }
        }
      }
      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const handlePriceBlur = (index) => {
    const line = lineItems[index];
    if (line.itemId && line.unitPrice > 0) {
      const item = items.find(i => i.id === line.itemId);
      if (item && item.minimumPrice > 0 && line.unitPrice < item.minimumPrice) {
        if (!window.confirm(`Price ${line.unitPrice} is below minimum price ${item.minimumPrice} for ${item.itemName}. Continue?`)) {
          setLineItems(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], unitPrice: item.minimumPrice };
            updated[index] = calculateLine(updated[index]);
            return updated;
          });
        }
      }
    }
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (formData.parentId) return;
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.quantity) > 0 && Number(line.unitPrice) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      docNumber: '', docDate: new Date().toISOString().split('T')[0],
      partyId: '', partyName: '',
      walkInName: '', walkInPhone: '',
      parentId: '', parentDocNumber: '', parentTranType: '', parentIds: [],
      customerPO: '',
      dueDate: '', paymentTerms: '', currency: activeCompany?.currencyCode || 'PKR',
      discountAmount: 0, whtRate: 0, whtAmount: 0, transportationCharges: 0, otherCharges: 0, notes: '', status: 'Draft',
      employeeId: '', employeeName: '', jobId: '', jobName: '',
      locationId: '', locationName: '',
      paymentAmount: 0, paymentAccountId: '', paymentAccountName: '',
      existingPaidAmount: 0,
      creditAccountId: '', creditAccountCode: '', creditAccountName: ''
    });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingInvoice(null);
    setSoProgress(null);
    soPickRef.current = '';
  };

  const handleCreate = () => { resetForm(); ensureFormData(); setViewMode('form'); };

  const handleEdit = (inv) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    ensureFormData(); // D3: load DC/SO link data on demand
    // Firebase cost: this invoice's linked DC(s) are now status 'Invoiced', so
    // they are NOT in the open-DC list. Fetch just those by id (a few reads, only
    // when opening a DC-linked invoice) so their chips render on the form.
    const linkedIds = Array.isArray(inv.parentIds) && inv.parentIds.length > 0
      ? inv.parentIds
      : (inv.parentTranType === 'DC' && inv.parentId ? [inv.parentId] : []);
    if (linkedIds.length > 0 && activeCompany?.id) {
      Promise.all(linkedIds.map(id => getTransaction(activeCompany.id, id).catch(() => null)))
        .then(docs => {
          const found = docs.filter(Boolean);
          if (found.length) setDeliveredDCs(prev => {
            const byId = {}; [...prev, ...found].forEach(d => { byId[d.id] = d; });
            return Object.values(byId);
          });
        });
    }
    setEditingInvoice(inv);
    setFormData({
      docNumber: inv.docNumber || '',
      docDate: inv.docDate || new Date().toISOString().split('T')[0],
      partyId: inv.partyId || '', partyName: inv.partyName || '',
      walkInName: inv.walkInName || '', walkInPhone: inv.walkInPhone || '',
      partyType: inv.partyType || 'customer',
      parentId: inv.parentId || '', parentDocNumber: inv.parentDocNumber || '',
      parentTranType: inv.parentTranType || '',
      parentIds: Array.isArray(inv.parentIds) && inv.parentIds.length > 0
        ? inv.parentIds
        : (inv.parentTranType === 'DC' && inv.parentId ? [inv.parentId] : []),
      customerPO: inv.customerPO || '',
      dueDate: inv.dueDate || '', paymentTerms: inv.paymentTerms || '',
      currency: inv.currency || 'PKR', discountAmount: inv.discountAmount || 0,
      whtRate: inv.whtRate || 0, whtAmount: inv.whtAmount || 0,
      transportationCharges: inv.transportationCharges || 0, otherCharges: inv.otherCharges || 0,
      notes: inv.notes || '', status: inv.status || 'Draft',
      employeeId: inv.employeeId || '', employeeName: inv.employeeName || '',
      jobId: inv.jobId || '', jobName: inv.jobName || '',
      locationId: inv.locationId || '', locationName: inv.locationName || '',
      paymentAmount: inv.paymentAmount || 0, paymentAccountId: inv.paymentAccountId || '', paymentAccountName: inv.paymentAccountName || '',
      existingReturnedAmount: Number(inv.returnedAmount) || 0, // ACC-11
      existingPaidAmount: Math.max(0, (inv.paidAmount || 0) - (inv.paymentAmount || 0)),
      creditAccountId: inv.creditAccountId || '',
      creditAccountCode: inv.creditAccountCode || '',
      creditAccountName: inv.creditAccountName || ''
    });
    setLineItems(inv.lines?.length > 0 ? inv.lines.map(l => ({ ...EMPTY_LINE, ...l, taxable: false, taxRate: 0, taxAmount: 0 })) : [{ ...EMPTY_LINE }]);
    // FLW-03: show / check what is left on the linked Sale Order (1 read).
    setSoProgress(null);
    soPickRef.current = (inv.parentTranType === 'SO' && inv.parentId) ? inv.parentId : '';
    if (inv.parentTranType === 'SO' && inv.parentId && activeCompany?.id) {
      (async () => {
        try {
          const snap = await getDoc(doc(db, 'companies', activeCompany.id, 'transactions', inv.parentId));
          if (!snap.exists() || snap.data().tranTypeCode !== 'SO') return;
          const so = { id: snap.id, ...snap.data() };
          const progress = await loadSOProgress(activeCompany.id, so, inv);
          if (soPickRef.current !== inv.parentId) return;
          setSoProgress(progress);
          // Older invoices: tag each line with the SO line it belongs to.
          const tags = tagSOLineNumbers(so.lines || [], inv.lines || [], progress);
          setLineItems(prev => prev.map((l, i) => (Number(l.soLineNumber) > 0 || !tags[i]) ? l : { ...l, soLineNumber: tags[i] }));
        } catch (e) { console.error('Error loading sale order progress:', e); }
      })();
    }
    if (inv.partyId) {
      loadCustomerPricing(activeCompany?.id, inv.partyId, isModuleEnabled)
        .then(setCustomerPricing)
        .catch(() => setCustomerPricing(EMPTY_PRICING));
    } else {
      setCustomerPricing(EMPTY_PRICING);
    }
    setViewMode('form');
  };

  const handleView = (inv) => { setViewingInvoice(inv); setViewMode('view'); };

  // Save invoice
  const handleSave = async (saveStatus) => {
    const _screenTimer = startSaveTimer('Whole click: Sale Invoice (Non-Tax)'); // save timer — see console
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.partyId) { alert('Please select a customer'); return; }
    if (Number(formData.paymentAmount) > 0 && !formData.paymentAccountId) {
      alert('Please select a payment account'); return;
    }
    // Zero price allowed (free items) — negative prices still left out. See utils/zeroPriceLines.js.
    const validLines = lineItems.filter(l => l.description && l.quantity > 0 && Number(l.unitPrice) >= 0);
    if (validLines.length === 0) {
      alert('Please add at least one line item with description, quantity, and price');
      return;
    }
    if (!confirmZeroPriceLines(validLines)) return;

    // Warn about zero cost items (profit will show 100%)
    const zeroCostItems = validLines.filter(l => !l.unitCost || l.unitCost === 0);
    if (zeroCostItems.length > 0 && saveStatus === 'Posted') {
      const itemNames = zeroCostItems.map(l => l.itemName || l.description).join(', ');
      const proceed = window.confirm(
        `Warning: The following items have ZERO cost price:\n\n${itemNames}\n\nProfit reports will show 100% margin for these items. Consider adding a purchase invoice first.\n\nDo you want to continue?`
      );
      if (!proceed) return;
    }

    // Validate financial year
    if (!activeFinancialYear?.id) {
      alert('No active financial year found. Please set an active financial year in Settings > Financial Year.');
      return;
    }

    // Validate batch/serial tracking
    for (const line of validLines) {
      if (line.trackingType === 'Batch' && !line.batchNumber?.trim()) {
        alert(`Batch selection is required for "${line.itemName || line.description}"`); return;
      }
      if (line.trackingType === 'Serial') {
        const serials = line.serialNumbers || [];
        const qty = line.quantity || 0;
        if (serials.length !== qty) {
          alert(`"${line.itemName || line.description}" requires exactly ${qty} serial number(s). You selected ${serials.length}.`); return;
        }
      }
    }

    // Discount cap validation (max 50%)
    for (const line of validLines) {
      if (Number(line.discountPercent) > 50) {
        alert(`Discount on "${line.itemName || line.description}" exceeds 50%. Please review.`); return;
      }
    }

    // FLW-03: never invoice more than what is left on the Sale Order.
    if (formData.parentTranType === 'SO' && formData.parentId && soProgress) {
      const overMsg = soOverDeliveryMessage(validLines, soProgress, formData.parentDocNumber);
      if (overMsg) { alert(overMsg); return; }
    }

    // Future date warning
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    // Zero-amount check when posting
    const preTotal = validLines.reduce((s, l) => s + (l.quantity * l.unitPrice), 0);
    if (saveStatus === 'Posted' && preTotal <= 0) {
      alert('Cannot post a zero-amount invoice'); return;
    }

    // Stock availability + Credit limit checks in PARALLEL (only when posting)
    if (saveStatus === 'Posted') {
      const stockItems = (!formData.parentId)
        ? validLines.filter(l => l.itemId).map(l => ({ itemId: l.itemId, itemName: l.itemName || l.description, quantity: l.quantity, unitCode: l.unitCode || l.unitOfMeasure }))
        : [];

      // Firebase cost: reuse the stock we ALREADY loaded for the on-screen badges
      // (+ cached items for unit conversion) instead of re-reading every item's
      // full stock history at Post. Same oversell check, 0 extra reads.
      const itemsById = {};
      items.forEach(i => { itemsById[i.id] = i; });

      const [insufficient, creditResult] = await Promise.all([
        stockItems.length > 0
          ? checkStockAvailability(activeCompany.id, stockItems, { stockMap: stockBalances, itemsMap: itemsById, branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null })
          : Promise.resolve([]),
        formData.partyId
          ? checkCustomerCreditLimit(activeCompany.id, formData.partyId, preTotal, editingInvoice?.id)
          : Promise.resolve({ exceeded: false })
      ]);

      if (insufficient.length > 0) {
        const msg = insufficient.map(i => `${i.itemName}: available ${i.available} ${i.unit || ''}, requested ${i.requested} ${i.unit || ''}`).join('\n');
        if (isModuleEnabled('inventory_block_negative')) {
          alert(`Cannot post — would drive stock below zero:\n\n${msg}`);
          return;
        }
        if (!window.confirm(`Insufficient stock:\n${msg}\n\nContinue anyway?`)) return;
      }

      if (creditResult.exceeded) {
        if (!window.confirm(`Customer credit limit exceeded!\nLimit: ${creditResult.creditLimit.toLocaleString()}\nOutstanding: ${creditResult.outstanding.toLocaleString()}\nNew total: ${creditResult.newTotal.toLocaleString()}\n\nContinue anyway?`)) return;
      }
    }

    // Large transaction warning
    if (isLargeTransaction(preTotal)) {
      if (!window.confirm(`This is a large transaction (${activeCompany?.currencySymbol || 'Rs.'} ${preTotal.toLocaleString()}). Please verify the amount. Continue?`)) return;
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => ({ ...calculateLine(l), lineNumber: i + 1 }));
      const status = saveStatus || formData.status || 'Draft';

      const invData = {
        tranTypeCode: TRAN_TYPE_CODE,
        docDate: formData.docDate,
        // Unified party: honor the picked party's real type for one shared ledger.
        partyType: formData.partyType || 'customer',
        partyId: formData.partyId,
        partyName: formData.partyName,
        // Per-invoice walk-in details — saved ONLY on this invoice (kept only for
        // the Walk-in customer; blank for anyone else). Never touches the customer
        // master or the ledger.
        walkInName: (customers.find(c => c.id === formData.partyId)?.isWalkIn) ? (formData.walkInName || '') : '',
        walkInPhone: (customers.find(c => c.id === formData.partyId)?.isWalkIn) ? (formData.walkInPhone || '') : '',
        parentId: formData.parentId || null,
        parentDocNumber: formData.parentDocNumber || '',
        parentTranType: formData.parentTranType || '',
        parentIds: formData.parentIds || [],
        customerPO: formData.customerPO || '',
        dueDate: formData.dueDate,
        paymentTerms: formData.paymentTerms,
        currency: formData.currency,
        subtotal: totals.subtotal,
        discountAmount: totals.headerDiscount,
        taxAmount: 0,
        whtRate: Number(formData.whtRate) || 0,
        whtAmount: totals.whtAmount,
        transportationCharges: totals.transportationCharges,
        otherCharges: totals.otherCharges,
        totalAmount: totals.totalAmount,
        paidAmount: (Number(formData.existingPaidAmount) || 0) + (Number(formData.paymentAmount) || 0),
        // ACC-11: returns reduce the balance but are not 'paid'; never below zero.
        balanceAmount: Math.max(0, totals.totalAmount - (Number(formData.existingPaidAmount) || 0) - (Number(formData.paymentAmount) || 0) - (Number(formData.existingReturnedAmount) || 0)),
        paymentAmount: Number(formData.paymentAmount) || 0,
        paymentAccountId: formData.paymentAccountId || null,
        paymentAccountName: formData.paymentAccountName || null,
        // Optional credit-account override → replaces Sales – General on the Cr leg
        // (honoured by generateSaleInvoiceEntries, which NSI shares with SI).
        creditAccountId: formData.creditAccountId || null,
        creditAccountCode: formData.creditAccountCode || null,
        creditAccountName: formData.creditAccountName || null,
        employeeId: formData.employeeId,
        employeeName: formData.employeeName,
        jobId: formData.jobId,
        jobName: formData.jobName,
        locationId: formData.locationId,
        locationName: formData.locationName,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null,
        notes: formData.notes,
        status,
        lines: calculatedLines,
        financialYearId: activeFinancialYear.id
      };

      if (status === 'Posted' && formData.status !== 'Posted') {
        invData.postedBy = currentUser.uid;
        invData.postedAt = new Date().toISOString();
      }

      let savedDocId = editingInvoice?.id;
      let savedDocNumber = editingInvoice?.docNumber;
      let newTranData = null;

      if (editingInvoice) {
        await updateTransaction(activeCompany.id, editingInvoice.id, invData, currentUser.uid);
      } else {
        invData.paidAmount = Number(formData.paymentAmount) || 0;
        invData.balanceAmount = totals.totalAmount - (Number(formData.paymentAmount) || 0);
        newTranData = await createTransaction(activeCompany.id, invData, currentUser.uid);
        savedDocId = newTranData.id;
        savedDocNumber = newTranData.docNumber;
      }

      // Firebase cost: keep the in-memory stock badges correct after a sale that
      // NEWLY reduced stock, so the next invoice this session checks against fresh
      // numbers (same trick POS uses). Direct invoices only — a DC-linked invoice
      // already moved stock when the DC posted. Re-posting an already-posted
      // invoice is skipped (net change is handled server-side; badge refreshes on
      // reopen).
      if (status === 'Posted' && formData.status !== 'Posted' && !formData.parentId) {
        setStockBalances(prev => {
          const next = { ...prev };
          calculatedLines.forEach(l => {
            if (!l.itemId) return;
            const it = items.find(i => i.id === l.itemId);
            if (it?.itemType === 'service') return;
            // On-demand stock: only adjust items whose balance we actually know.
            // Decrementing an unknown item would invent a bogus (negative) figure
            // that the next availability check would trust — leave it unknown and
            // let it be fetched fresh when needed.
            if (next[l.itemId] === undefined) return;
            const { requestedBase } = toBaseQty(it || {}, l.quantity, l.unitCode || l.unitOfMeasure);
            next[l.itemId] = Number(next[l.itemId]) - requestedBase;
          });
          return next;
        });
      }

      // Update each linked DC's status to 'Invoiced' when posting; revert dropped ones to 'Delivered'.
      if (formData.parentTranType === 'DC' && status === 'Posted') {
        try {
          const newIds = formData.parentIds || [];
          const oldIds = (editingInvoice?.parentIds && editingInvoice.parentIds.length > 0)
            ? editingInvoice.parentIds
            : (editingInvoice?.parentTranType === 'DC' && editingInvoice?.parentId ? [editingInvoice.parentId] : []);
          const removedIds = oldIds.filter(id => !newIds.includes(id));
          // Speed: all linked challans are updated at the same time (was one by one).
          await Promise.all([
            ...removedIds.map(dcId => updateTransactionStatus(activeCompany.id, dcId, 'Delivered', currentUser.uid)),
            ...newIds.map(dcId => updateTransactionStatus(activeCompany.id, dcId, 'Invoiced', currentUser.uid)),
          ]);
        } catch (err) { console.error('Error updating DC status:', err); }
      }

      // Update parent SO line quantities and status if linked directly (without
      // DC) — matched by the SO line each invoice line came from (FLW-03); an edit
      // takes the invoice's previous quantities off first. Only a posted invoice counts.
      if ((formData.parentId && formData.parentTranType === 'SO') || (editingInvoice && editingInvoice.parentTranType === 'SO')) {
        try {
          await syncInvoiceSOProgress(activeCompany.id, editingInvoice,
            { parentId: formData.parentId, parentTranType: formData.parentTranType, status, lines: calculatedLines },
            currentUser.uid);
        } catch (err) { console.error('Error updating Sale Order status:', err); }
      }

      // SubTransactions + StockFlow already generated by createTransaction/updateTransaction above.
      // No duplicate calls needed here.

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedRecord = editingInvoice
        ? { ...editingInvoice, ...invData, id: savedDocId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingInvoice
        ? prev.map(r => r.id === savedDocId ? savedRecord : r)
        : [savedRecord, ...prev]);

      // Stock moved — drop the per-item cached quantities so the next "stock?"
      // lookup (here or on any other form this session) reads fresh numbers.
      invalidateItemStock(activeCompany.id);

      setViewMode('list');
      resetForm();

      // The DC(s) this invoice just consumed are now 'Invoiced' — drop them from
      // the open-DC picker so they can't be linked again this session (no re-scan
      // of the invoice history needed).
      const usedDCIds = (savedRecord?.parentIds && savedRecord.parentIds.length > 0)
        ? savedRecord.parentIds
        : (savedRecord?.parentTranType === 'DC' && savedRecord?.parentId ? [savedRecord.parentId] : []);
      if (savedRecord?.status === 'Posted' && usedDCIds.length > 0) {
        const used = new Set(usedDCIds);
        setDeliveredDCs(prev => prev.filter(d => !used.has(d.id)));
      }

      // Background refresh — reconciles with server data without blocking UI.
      loadInvoices().catch(err =>
        console.error('Background refresh failed:', err)
      );
    } catch (error) {
      console.error('Error saving non-tax sale invoice:', error);
      alert('Error saving non-tax sale invoice: ' + error.message);
    } finally {
      _screenTimer.end();
      setSaving(false);
    }
  };

  const handleDelete = async (inv) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete Invoice ${inv.docNumber}?`)) return;
    try {
      // ACC-04 fix (2026-09-30): deleteTransaction() runs the safety checks FIRST
      // (linked documents, receipts, FBR) and only then removes ledger + stock rows.
      // Wiping them here beforehand left a blocked document with no accounting.
      await deleteTransaction(activeCompany.id, inv.id);
      await reverseSODelivery(inv);
      await reverseDCInvoicing(inv);
      // D3: DC statuses changed — mark the form link-data stale so the NEXT
      // form open refetches it, instead of paying for it here on the list view.
      formDataLoadedRef.current = false;
      await loadInvoices();
    } catch (error) { console.error('Error deleting invoice:', error); alert('Could not delete: ' + (error?.message || 'unknown error')); }
  };

  const handleCancel = async (inv) => {
    if (!canCancel) { alert('You do not have permission to cancel.'); return; }
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (inv.status === 'Paid' || inv.status === 'Cancelled') {
      alert('This invoice cannot be cancelled');
      return;
    }
    if ((inv.paidAmount || 0) > 0) {
      alert('Cannot cancel an invoice with payments. Reverse payments first.');
      return;
    }
    if (!window.confirm(`Cancel Invoice ${inv.docNumber}?`)) return;
    try {
      // Delete sub-transactions and stock flow if posted
      if (inv.status === 'Posted' || inv.status === 'Partial') {
        await deleteSubTransactions(activeCompany.id, inv.id);
        await deleteStockFlow(activeCompany.id, inv.id);
      }
      await updateTransactionStatus(activeCompany.id, inv.id, 'Cancelled', currentUser.uid);
      await reverseSODelivery(inv);
      await reverseDCInvoicing(inv);
      formDataLoadedRef.current = false; // D3: refresh link data on next form open
      await loadInvoices();
    } catch (error) { console.error('Error cancelling invoice:', error); }
  };

  // Revert linked DC(s) back to 'Delivered' when this SI is deleted or cancelled.
  const reverseDCInvoicing = async (inv) => {
    if (inv.parentTranType !== 'DC') return;
    const ids = (inv.parentIds && inv.parentIds.length > 0)
      ? inv.parentIds
      : (inv.parentId ? [inv.parentId] : []);
    if (ids.length === 0) return;
    try {
      for (const dcId of ids) {
        await updateTransactionStatus(activeCompany.id, dcId, 'Delivered', currentUser.uid);
      }
    } catch (err) { console.error('Error reverting DC status:', err); }
  };

  // Reverse SO deliveredQty when SI is deleted or cancelled (only a posted
  // invoice had added to the SO; matched by the SO line each line came from).
  const reverseSODelivery = async (inv) => {
    if (!inv.parentId || inv.parentTranType !== 'SO') return;
    try {
      await syncInvoiceSOProgress(activeCompany.id, inv, null, currentUser.uid);
    } catch (err) { console.error('Error reversing SO delivery:', err); }
  };

  // Print
  // Invoice Letterhead Choice (advance module): Print first asks whose details
  // go at the top — the company's, another saved name, or Blank.
  //   letterhead undefined → ask;  null → company (as before);  object → that one.
  const handlePrint = async (inv, letterhead) => {
    if (letterheadChoiceOn && letterhead === undefined) { setLetterheadPickFor({ inv: inv || viewingInvoice }); return; }
    const target = inv || viewingInvoice;
    const cust = customers.find(c => c.id === target?.partyId);
    const enrichedLines = (target?.lines || []).map(l => {
      const it = items.find(i => i.id === l.itemId);
      // Barcode from the item master (print-only, 0 reads) — printed only when
      // Print Settings → Non-Tax Sale Invoice → Barcode is switched on (2026-11-02).
      const unitBarcode = (it?.units || []).find(u => u.unitCode === (l.unitCode || l.unitOfMeasure))?.barcode;
      return { ...l, imageUrl: it?.imageUrl || l.imageUrl || '', barcode: unitBarcode || it?.barcode || l.barcode || '' };
    });
    // Previous balance (Print Settings → Non-Tax Sale Invoice → Previous / New
    // Balance, off by default) — same as the Sale Invoice: this customer only, on demand.
    let previousBalance = null;
    if (printConfig?.nonTaxSaleInvoice?.showPreviousBalance && target?.partyId) {
      let cur = NaN;
      try {
        cur = Number(await getCustomerBalance(
          activeCompany.id,
          target.partyId,
          activeBranch && activeBranch.id !== 'all' ? activeBranch.id : null,
          activeFinancialYear?.id || null
        ));
      } catch { /* balance unavailable — print without it */ }
      if (!Number.isNaN(cur)) {
        const total = Number(target.totalAmount) || 0;
        const paid = Number(target.paidAmount) || 0;
        const inLedger = ['Posted', 'Partial', 'Paid'].includes(target.status);
        previousBalance = inLedger ? cur - (total - paid) : cur;
      }
    }
    // Resolve the customer's area/region names (IDs on the customer) for the printout.
    const { areas, regions } = await getAreasRegionsCached(activeCompany.id);
    const partyArea = areas.find(a => a.id === cust?.areaId)?.areaName || '';
    const partyRegion = regions.find(r => r.id === cust?.regionId)?.regionName || '';
    const enriched = {
      ...target, lines: enrichedLines,
      partyNTN: cust?.ntn || target?.partyNTN || '',
      partySTRN: cust?.strn || target?.partySTRN || '',
      partyAddress: cust?.address || target?.partyAddress || '',
      partyPhone: cust?.phone || cust?.mobile || target?.partyPhone || '',
      partyArea,
      partyRegion,
      ...(previousBalance != null ? { previousBalance } : {}),
    };
    printDocument('nonTaxSaleInvoice', enriched, activeCompany, printConfig, activeBranch, letterhead ? { letterhead } : undefined);
  };

  // Keyboard shortcuts
  useDocumentShortcuts({
    onNew: canEdit ? handleCreate : undefined,
    onSave: canEdit ? () => handleSave('Posted') : undefined,
    viewMode,
    saving
  });

  // ===== RENDER =====

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox>
          <FiSearch />
          <input type="text" placeholder="Search by invoice#, customer, DC#, order#..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Posted">Posted</option>
            <option value="Partial">Partial</option>
            <option value="Paid">Paid</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Invoice</TranAddButton>}
        </FilterGroup>
      </ActionBar>

      {loading ? (
        <LoadingMessage>Loading non-tax sale invoices...</LoadingMessage>
      ) : filteredInvoices.length === 0 ? (
        <TranEmptyState>
          <FiFileText size={48} />
          <h3>No Non-Tax Sale Invoices</h3>
          <p>{invoices.length === 0 ? 'Create your first non-tax sale invoice' : 'No invoices match your filters'}</p>
          {invoices.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Invoice</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer>
          <SortContext.Provider value={sort.ctxValue}>
          <Table>
            <thead>
              <tr>
                <SortableTh field="docNumber">Invoice #</SortableTh>
                <SortableTh field="docDate">Date</SortableTh>
                {showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}
                <SortableTh field="partyName">Customer</SortableTh>
                <SortableTh field="parentDocNumber">Parent Ref</SortableTh>
                <SortableTh field="totalAmount" style={{ textAlign: 'right' }}>Total</SortableTh>
                <SortableTh field="balanceAmount" style={{ textAlign: 'right' }}>Balance</SortableTh>
                <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
                <Th style={{ textAlign: 'center' }}>Links</Th>
                <Th style={{ textAlign: 'center' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map(inv => (
                <Tr key={inv.id}>
                  <Td><strong>{inv.docNumber}</strong></Td>
                  <Td>{inv.docDate}</Td>
                  {showBranchCol && <Td>{inv.branchName || '-'}</Td>}
                  <Td>{inv.partyName}</Td>
                  <Td>
                    <LinkedDocNumbers {...parentLinks(inv)} showIcon={false} fallback={<span style={{ color: colors.text.tertiary, fontStyle: 'italic' }}>Direct</span>} />
                  </Td>
                  <Td style={{ textAlign: 'right', fontWeight: 500 }}>
                    {activeCompany?.currencyCode || 'PKR'} {formatAmount(inv.totalAmount)}
                  </Td>
                  <Td style={{ textAlign: 'right', fontWeight: 500, color: (inv.balanceAmount || 0) > 0 ? colors.error.main : colors.success.main }}>
                    {formatAmount(inv.balanceAmount ?? inv.totalAmount)}
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <StatusBadge $color={STATUS_COLORS[inv.status]}>{inv.status}</StatusBadge>
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <LinkedBadge label={inv.parentTranType || 'From'} {...parentLinks(inv)} />
                    <LinkedBadge label="NSR" ids={inv.childIds} />
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <ActionGroup>
                      <IconBtn title="View" onClick={() => handleView(inv)}><FiEye /></IconBtn>
                      {canChange && inv.status !== 'Cancelled' && <IconBtn title="Edit" onClick={() => handleEdit(inv)}><FiEdit2 /></IconBtn>}
                      <IconBtn title="Print" onClick={() => handlePrint(inv)}><FiPrinter /></IconBtn>
                      {inv.status !== 'Draft' && inv.status !== 'Cancelled' && (
                        <SendWhatsAppButton templateKey="nonTaxSaleInvoice" doc={inv} party={customers.find(c => c.id === inv.partyId)} variant="icon" />
                      )}
                      {canDelete && inv.status !== 'Cancelled' && <IconBtn title="Delete" $danger onClick={() => handleDelete(inv)}><FiTrash2 /></IconBtn>}
                      {canCancel && canEdit && inv.status !== 'Paid' && inv.status !== 'Cancelled' && (
                        <IconBtn title="Cancel" $danger onClick={() => handleCancel(inv)}><FiXCircle /></IconBtn>
                      )}
                    </ActionGroup>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          </SortContext.Provider>
        </TableContainer>
      )}

      <DocListPagination {...pagination} />

      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredInvoices.length : pagination.totalCount}</strong> invoice(s)</span>
        <span>
          Outstanding: <strong>{formatAmount(listSummary.outstanding)}</strong> |{' '}
          Draft: <strong>{listSummary.counts.Draft || 0}</strong> |{' '}
          Posted: <strong>{listSummary.counts.Posted || 0}</strong> |{' '}
          Paid: <strong>{listSummary.counts.Paid || 0}</strong>
        </span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingInvoice) return null;
    const inv = viewingInvoice;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingInvoice(null); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>
            {inv.docNumber}
            <StatusBadge $color={STATUS_COLORS[inv.status]} style={{ marginLeft: 12 }}>{inv.status}</StatusBadge>
          </FormTitle>
          <FormActions>
            {canChange && inv.status !== 'Cancelled' && <SaveButton onClick={() => handleEdit(inv)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(inv)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Invoice Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Invoice Number</label><span>{inv.docNumber}</span></InfoItem>
            <InfoItem><label>Invoice Date</label><span>{inv.docDate}</span></InfoItem>
            <InfoItem><label>Customer</label><span>{inv.partyName}</span></InfoItem>
            <InfoItem><label>Parent Reference</label><span>{inv.parentDocNumber || 'Direct Invoice'}</span></InfoItem>
            <InfoItem><label>Customer PO #</label><span>{inv.customerPO || '-'}</span></InfoItem>
            <InfoItem><label>Due Date</label><span>{inv.dueDate || '-'}</span></InfoItem>
            <InfoItem><label>Payment Terms</label><span>{inv.paymentTerms || '-'}</span></InfoItem>
            <InfoItem><label>Currency</label><span>{activeCompany?.currencyCode || 'PKR'}</span></InfoItem>
            <InfoItem><label>Employee</label><span>{inv.employeeName || '-'}</span></InfoItem>
            <InfoItem><label>Job</label><span>{inv.jobName || '-'}</span></InfoItem>
            <InfoItem><label>Location</label><span>{inv.locationName || '-'}</span></InfoItem>
          </InfoGrid>
          {(inv.parentId || inv.childIds?.length > 0) && (
            <LinkedDocsSection>
              <LinkedBadge label={`Linked ${inv.parentTranType || 'Doc'}`} {...parentLinks(inv)} max={0} />
              <LinkedBadge label="Linked NSR" ids={inv.childIds} max={0} />
            </LinkedDocsSection>
          )}
        </FormCard>

        <FormCard>
          <SectionTitle>Line Items</SectionTitle>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>#</Th><Th style={{ width: 40 }}></Th><Th>Description</Th>
                  <Th style={{ textAlign: 'right' }}>Qty</Th>
                  <Th style={{ textAlign: 'right' }}>Unit Price</Th>
                  <Th style={{ textAlign: 'right' }}>Disc %</Th>
                  <Th style={{ textAlign: 'right' }}>Total</Th>
                </tr>
              </thead>
              <tbody>
                {(inv.lines || []).map((line, i) => {
                  const itm = items.find(it => it.id === line.itemId);
                  return (
                  <Tr key={i}>
                    <Td>{i + 1}</Td>
                    <Td style={{ padding: '2px 4px' }}>
                      {itm?.imageUrl ? <ItemThumb src={itm.imageUrl} alt="" /> : <ItemThumbEmpty><FiPackage size={14} /></ItemThumbEmpty>}
                    </Td>
                    <Td>{line.description}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.quantity}{line.unitOfMeasure ? <span style={{ fontSize: '11px', color: '#64748B', marginLeft: '4px' }}>{line.unitOfMeasure}</span> : ''}<TwoUnitNote line={line} /></Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(line.unitPrice)}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.discountPercent || 0}%</Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(line.lineTotal)}</Td>
                  </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableContainer>
          <TotalsSection>
            <TotalsTable>
              <tbody>
                <tr><td>Subtotal</td><td>{formatAmount(inv.subtotal)}</td></tr>
                {inv.discountAmount > 0 && <tr><td>Discount</td><td>-{formatAmount(inv.discountAmount)}</td></tr>}
                {(() => {
                  const m = (inv.lines || []).reduce((s, l) => s + (Number(l.mrp) || 0), 0);
                  return m > 0 ? <tr><td>MRP</td><td>{formatAmount(m)}</td></tr> : null;
                })()}
                {inv.whtAmount > 0 && <tr><td>WHT ({inv.whtRate || 0}%)</td><td>-{formatAmount(inv.whtAmount)}</td></tr>}
                {Number(inv.transportationCharges) > 0 && <tr><td>Transportation / Carriage</td><td>{formatAmount(inv.transportationCharges)}</td></tr>}
                {Number(inv.otherCharges) > 0 && <tr><td>Other Charges</td><td>{formatAmount(inv.otherCharges)}</td></tr>}
                <TotalRow><td>Total ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(inv.totalAmount)}</td></TotalRow>
                <tr><td>Paid</td><td style={{ color: colors.success.main }}>{formatAmount(inv.paidAmount || 0)}</td></tr>
                <BalanceRow><td>Balance Due</td><td>{formatAmount(inv.balanceAmount ?? inv.totalAmount)}</td></BalanceRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>
        </FormCard>

        <AttachmentPanel
          transactionId={inv?.id}
          attachment={inv?.attachment}
          canEdit={false}
        />

        {inv.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{inv.notes}</NotesText></FormCard>}
      </>
    );
  };

  const renderFormView = () => {
    // Linkable DCs = the open (Delivered/Dispatched) DCs for this customer. A DC
    // leaves this list the moment it is invoiced (its status flips to 'Invoiced'),
    // so no cross-invoice scan is needed to exclude already-invoiced ones.
    // Only this branch's challans / orders (see utils/branchLink.js).
    const customerDCs = deliveredDCs
      .filter(d => !formData.partyId || d.partyId === formData.partyId)
      .filter(d => inActiveBranch(d, activeBranch));
    const customerSOs = approvedSOs.filter(s => !formData.partyId || s.partyId === formData.partyId)
      .filter(s => inActiveBranch(s, activeBranch, formData.parentTranType === 'SO' ? formData.parentId : null));
    const isLinked = !!formData.parentId;
    const isMultiDC = formData.parentTranType === 'DC' && (formData.parentIds?.length || 0) > 0;
    const linkedDCObjects = (formData.parentIds || []).map(id => deliveredDCs.find(d => d.id === id)).filter(Boolean);
    const availableDCs = customerDCs.filter(d => !formData.parentIds?.includes(d.id));
    const hasSourcedLines = lineItems.some(l => !!l.sourceDocId);
    // FLW-03: Ordered / Already Delivered / Remaining columns for an invoice made from a Sale Order.
    const soCols = formData.parentTranType === 'SO' && !!formData.parentId && !!soProgress;

    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); resetForm(); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>{editingInvoice ? `Edit: ${editingInvoice.docNumber}` : 'New Non-Tax Sale Invoice'}</FormTitle>
          <FormActions>
            <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}>
              <FiX /> Cancel
            </SaveButton>
            {canEdit && !(editingInvoice && editingInvoice.status && editingInvoice.status !== 'Draft' && editingInvoice.status !== 'Cancelled') && (
              <SaveButton onClick={() => handleSave('Draft')} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save Draft'}
              </SaveButton>
            )}
            {canEdit && (formData.status === 'Draft' || !editingInvoice) && (
              <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}>
                <FiCheck /> Post Invoice
              </SaveButton>
            )}
            {/* ACC-05: editing a posted document keeps it posted (no silent drop to Draft) */}
            {canEdit && (editingInvoice && editingInvoice.status && editingInvoice.status !== 'Draft' && editingInvoice.status !== 'Cancelled') && (
              <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}><FiCheck /> {saving ? 'Saving...' : 'Save Changes'}</SaveButton>
            )}
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Invoice Details</SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Invoice Number</Label>
              <Input value={editingInvoice?.docNumber || 'Auto-generated'} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Invoice Date *</Label>
              <Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>Status</Label>
              <Input value={formData.status} disabled />
            </FormGroup>
            <FormGroup $span2>
              <Label>Link to Delivery Challan(s) {isMultiDC ? `(${linkedDCObjects.length})` : ''}</Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                {linkedDCObjects.map(d => (
                  <span key={d.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: colors.primary.main + '15', color: colors.primary.main,
                    border: `1px solid ${colors.primary.main}40`,
                    padding: '4px 8px', borderRadius: 12, fontSize: 12, fontWeight: 500
                  }}>
                    {d.docNumber}
                    <FiX size={12} style={{ cursor: 'pointer' }} onClick={() => removeDC(d.id)} title="Remove this DC" />
                  </span>
                ))}
                <Select
                  value=""
                  onChange={e => { addDC(e.target.value); e.target.value = ''; }}
                  disabled={formData.parentTranType === 'SO'}
                  style={{ minWidth: 160, flex: linkedDCObjects.length === 0 ? 1 : '0 0 auto' }}
                >
                  <option value="">{linkedDCObjects.length === 0 ? '-- Direct Invoice (No DC) --' : '+ Add another DC'}</option>
                  {availableDCs.map(d => (
                    <option key={d.id} value={d.id}>{d.docNumber} - {d.partyName} ({d.docDate || ''})</option>
                  ))}
                </Select>
              </div>
            </FormGroup>
            <FormGroup>
              <Label>Link to Sale Order</Label>
              <Select value={formData.parentTranType === 'SO' ? formData.parentId : ''} onChange={e => handleSOChange(e.target.value)} disabled={isMultiDC}>
                <option value="">-- No Sale Order --</option>
                {customerSOs.map(s => (
                  <option key={s.id} value={s.id}>{s.docNumber} - {s.partyName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup $span2>
              <Label>Customer *</Label>
              <CustomerSelect customers={customers} selectedCustomerId={formData.partyId} onChange={handleCustomerChange} disabled={isLinked || isMultiDC} balanceOnDemand />
            </FormGroup>
            {customers.find(c => c.id === formData.partyId)?.isWalkIn && (
              <>
                <FormGroup>
                  <Label>Walk-in Customer Name (optional)</Label>
                  <Input value={formData.walkInName} onChange={e => handleHeaderChange('walkInName', e.target.value)}
                    placeholder="Name for this invoice only" disabled={isLinked || isMultiDC} />
                </FormGroup>
                <FormGroup>
                  <Label>Walk-in Phone (optional)</Label>
                  <Input value={formData.walkInPhone} onChange={e => handleHeaderChange('walkInPhone', e.target.value)}
                    placeholder="Phone for this invoice only" disabled={isLinked || isMultiDC} />
                </FormGroup>
              </>
            )}
            <FormGroup>
              <Label>Due Date</Label>
              <Input type="date" value={formData.dueDate} onChange={e => handleHeaderChange('dueDate', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>Payment Terms</Label>
              <Input value={formData.paymentTerms} onChange={e => handleHeaderChange('paymentTerms', e.target.value)} placeholder="e.g., 30 days" />
            </FormGroup>
            <FormGroup>
              <Label>Customer PO # (optional)</Label>
              <Input value={formData.customerPO} onChange={e => handleHeaderChange('customerPO', e.target.value)} placeholder="Customer's PO number" />
            </FormGroup>
            <FormGroup>
              <Label>Parent Reference</Label>
              <Input value={formData.parentDocNumber} disabled placeholder="From linked document" />
            </FormGroup>
            <FormGroup>
              <Label>Currency</Label>
              <Input value={activeCompany?.currencyCode || 'PKR'} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Employee</Label>
              <Select value={formData.employeeId} onChange={e => {
                const emp = employees.find(em => em.id === e.target.value);
                handleHeaderChange('employeeId', e.target.value);
                handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
              }}>
                <option value="">-- Select Employee --</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Job</Label>
              <Select value={formData.jobId} onChange={e => {
                const job = jobs.find(j => j.id === e.target.value);
                handleHeaderChange('jobId', e.target.value);
                handleHeaderChange('jobName', job?.jobName || '');
              }}>
                <option value="">-- Select Job --</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>{reqLabel(activeCompany, 'Location')}</Label>
              <Select value={formData.locationId} onChange={e => {
                const loc = locations.find(l => l.id === e.target.value);
                handleHeaderChange('locationId', e.target.value);
                handleHeaderChange('locationName', loc?.locationName || '');
              }} disabled={!!formData.dcId}>
                <option value="">-- Select Location --</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.locationName}</option>
                ))}
              </Select>
            </FormGroup>
          </FormGrid>
        </FormCard>

        <FormCard>
          <LineItemsHeader>
            <SectionTitle style={{ margin: 0 }}>Line Items</SectionTitle>
            {!isLinked && !isMultiDC && <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>}
          </LineItemsHeader>

          <LineTableContainer>
            <LineTable>
              <thead>
                <tr>
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
                  {hasSourcedLines && <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>DC #</LineTh>}
                  {hasSourcedLines && <LineTh style={{ width: 110, minWidth: 110, maxWidth: 110 }}>DC Date</LineTh>}
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
                  <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Item</LineTh>
                  <LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Description</LineTh>
                  {soCols && <LineTh style={{ width: 110, minWidth: 110, maxWidth: 110 }}>Ordered</LineTh>}
                  {soCols && <LineTh style={{ width: 110, minWidth: 110, maxWidth: 110 }}>Already Delivered</LineTh>}
                  {soCols && <LineTh style={{ width: 110, minWidth: 110, maxWidth: 110 }}>Remaining</LineTh>}
                  <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Qty</LineTh>
                  <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Price Type</LineTh>
                  <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Unit Price</LineTh>
                  <LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Disc %</LineTh>
                  {mrpEnabled && <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>MRP</LineTh>}
                  <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</LineTh>
                  {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 160, minWidth: 160, maxWidth: 160 }}>Batch</LineTh>}
                  {lineItems.some(l => l.trackingType === 'Serial') && <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Serial Numbers</LineTh>}
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((line, index) => {
                  const calc = calculateLine(line);
                  const isSourced = !!line.sourceDocId;
                  const soP = soCols ? soProgress[line.soLineNumber] : null;
                  const soRemaining = soP ? Math.max(0, r6(soP.ordered - soP.delivered)) : null;
                  const soOver = soRemaining !== null && (Number(line.quantity) || 0) > soRemaining + 1e-6;
                  return (
                    <React.Fragment key={index}><tr>
                      <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                      {hasSourcedLines && <LineTd style={{ fontSize: 11, color: colors.text.secondary }}>{line.sourceDocNumber || '-'}</LineTd>}
                      {hasSourcedLines && <LineTd style={{ fontSize: 11, color: colors.text.secondary }}>{line.sourceDocDate || '-'}</LineTd>}
                      <LineTd style={{ padding: '2px 4px' }}>
                        {(() => { const itm = items.find(i => i.id === line.itemId); return itm?.imageUrl ? (
                          <ItemThumb src={itm.imageUrl} alt="" />
                        ) : line.itemId ? (
                          <ItemThumbEmpty><FiPackage size={14} /></ItemThumbEmpty>
                        ) : null; })()}
                      </LineTd>
                      <LineTd>
                        <ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={filterItemsByPricing(items, customerPricing)} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} disabled={isLinked || isSourced} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} />
                      </LineTd>
                      <LineTd>
                        <LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)}
                          placeholder="Description" disabled={isLinked || isSourced} />
                        
                      </LineTd>
                      {soCols && <LineTd><LineInput type="number" value={soP ? soP.ordered : ''} placeholder="-" disabled style={{ textAlign: 'right', background: colors.neutral[100] }} /></LineTd>}
                      {soCols && <LineTd><LineInput type="number" value={soP ? soP.delivered : ''} placeholder="-" disabled style={{ textAlign: 'right', background: colors.neutral[100] }} /></LineTd>}
                      {soCols && <LineTd><LineInput type="number" value={soRemaining === null ? '' : soRemaining} placeholder="-" disabled style={{ textAlign: 'right', background: colors.neutral[100] }} /></LineTd>}
                      <LineTd>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <LineInput type="number" min="0" value={line.quantity}
                            onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))}
                            disabled={isSourced || line.hasSize}
                            title={line.hasSize ? 'Auto-computed from L × H × Pieces' : undefined}
                            style={{ textAlign: 'right', background: line.hasSize ? '#F1F5F9' : undefined, color: soOver ? colors.error.main : undefined }} />
                          <LineUnitPicker
                            item={items.find(i => i.id === line.itemId)}
                            line={line}
                            onChange={(uc) => handleLineChange(index, 'unitCode', uc)}
                          />
                        </div>
                        <TwoUnitQtyBox item={items.find(i => i.id === line.itemId)} unitCode={line.unitCode || line.unitOfMeasure}
                          quantity={line.quantity} hasSize={line.hasSize} disabled={isSourced}
                          onChange={(q) => handleLineChange(index, 'quantity', q)} />
                      </LineTd>
                      <LineTd>
                        <Select value={line.priceType || 'Retail'} onChange={e => handleLineChange(index, 'priceType', e.target.value)} style={{ padding: '4px 6px', fontSize: '12px', minWidth: 80 }}>
                          <option value="Retail">Retail</option>
                          <option value="Wholesale">Wholesale</option>
                          <option value="Dealer">Dealer</option>
                        </Select>
                      </LineTd>
                      <LineTd>
                        <LineInput type="number" min="0" step="0.01" value={line.unitPrice}
                          onChange={e => handleLineChange(index, 'unitPrice', Number(e.target.value))}
                          onBlur={() => handlePriceBlur(index)} style={{ textAlign: 'right' }} />
                      </LineTd>
                      <LineTd>
                        <LineInput type="number" min="0" max="100" value={line.discountPercent}
                          onChange={e => handleLineChange(index, 'discountPercent', Number(e.target.value))}
                          onKeyDown={(e) => handleLastFieldEnter(e, index)}
                          style={{ textAlign: 'right' }} />
                      </LineTd>
                      {mrpEnabled && (
                        <LineTd>
                          <LineInput type="number" min="0" step="0.01" value={line.mrp || 0}
                            onChange={e => handleLineChange(index, 'mrp', Number(e.target.value))}
                            style={{ textAlign: 'right' }} />
                        </LineTd>
                      )}
                      <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
                      {lineItems.some(l => l.trackingType === 'Batch') && (
                        <LineTd>{line.trackingType === 'Batch' ? (
                          <Select value={line.batchNumber} onChange={e => handleLineChange(index, 'batchNumber', e.target.value)} style={{ padding: '6px 8px', fontSize: '13px', minWidth: 120 }}>
                            <option value="">Select Batch</option>
                            {(availableBatches[line.itemId] || []).map(b => (
                              <option key={b.batchNumber} value={b.batchNumber}>{b.batchNumber} (Qty: {b.remaining}{b.expiryDate ? `, Exp: ${b.expiryDate}` : ''})</option>
                            ))}
                          </Select>
                        ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
                      )}
                      {lineItems.some(l => l.trackingType === 'Serial') && (
                        <LineTd>{line.trackingType === 'Serial' ? (
                          <SerialSelectPopover
                            availableSerials={availableSerials[line.itemId] || []}
                            selectedSerials={line.serialNumbers || []}
                            requiredCount={line.quantity || 0}
                            onChange={(serials) => handleLineChange(index, 'serialNumbers', serials)}
                            disabled={!!formData.parentId}
                          />
                        ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
                      )}
                      <LineTd style={{ textAlign: 'center' }}>
                        {!isLinked && lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)} title="Remove"><FiX /></RemoveLineBtn>}
                      </LineTd>
                    </tr>
                    {line.hasSize && (<SizeLineRow line={line} index={index} onChange={handleLineChange} disabled={isLinked || isSourced} leftColSpan={3 + (hasSourcedLines ? 2 : 0)} />)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </LineTable>
          </LineTableContainer>

          <TotalsSection>
            <TotalsTable>
              <tbody>
                <tr><td>Subtotal</td><td>{formatAmount(totals.subtotal)}</td></tr>
                <tr>
                  <td>Discount</td>
                  <td><DiscountInput type="number" min="0" step="0.01" value={formData.discountAmount} onChange={e => handleHeaderChange('discountAmount', Number(e.target.value))} /></td>
                </tr>
                {mrpEnabled && totals.mrpTotal > 0 && (
                  <tr><td>MRP</td><td>{formatAmount(totals.mrpTotal)}</td></tr>
                )}
                <tr>
                  <td>WHT %</td>
                  <td><DiscountInput type="number" min="0" max="100" step="0.5" value={formData.whtRate} onChange={e => handleHeaderChange('whtRate', Number(e.target.value))} /></td>
                </tr>
                {totals.whtAmount > 0 && <tr><td>WHT Amount</td><td>-{formatAmount(totals.whtAmount)}</td></tr>}
                <tr>
                  <td>Transportation / Carriage</td>
                  <td><DiscountInput type="number" min="0" step="0.01" value={formData.transportationCharges} onChange={e => handleHeaderChange('transportationCharges', Number(e.target.value))} /></td>
                </tr>
                <tr>
                  <td>Other Charges</td>
                  <td><DiscountInput type="number" min="0" step="0.01" value={formData.otherCharges} onChange={e => handleHeaderChange('otherCharges', Number(e.target.value))} /></td>
                </tr>
                <TotalRow><td>Total ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(totals.totalAmount)}</td></TotalRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>

          <PaymentSection>
            <SectionTitle>Payment</SectionTitle>
            <FormGrid>
              {formData.existingPaidAmount > 0 && (
                <FormGroup>
                  <Label>Received via Vouchers</Label>
                  <Input value={formatAmount(formData.existingPaidAmount)} disabled
                    style={{ fontWeight: 600, color: colors.success.main }} />
                </FormGroup>
              )}
              <FormGroup>
                <Label>Amount Received</Label>
                <Input type="number" min="0" step="0.01" value={formData.paymentAmount}
                  onChange={e => handleHeaderChange('paymentAmount', Math.min(Number(e.target.value), totals.totalAmount - formData.existingPaidAmount))}
                  placeholder="0.00" />
              </FormGroup>
              <FormGroup>
                <Label>Payment Account</Label>
                <Select value={formData.paymentAccountId} onChange={e => {
                  const acc = paymentAccounts.find(a => a.id === e.target.value);
                  handleHeaderChange('paymentAccountId', e.target.value);
                  handleHeaderChange('paymentAccountName', acc?.name || '');
                }}>
                  <option value="">-- Select Account --</option>
                  <optgroup label="Cash Accounts">
                    {paymentAccounts.filter(a => a.code?.startsWith('11101')).map(a => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Bank Accounts">
                    {paymentAccounts.filter(a => a.code?.startsWith('11102')).map(a => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </optgroup>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Credit Account (override)</Label>
                <Select value={formData.creditAccountId} onChange={e => {
                  const acc = allAccounts.find(a => a.id === e.target.value);
                  handleHeaderChange('creditAccountId', e.target.value);
                  handleHeaderChange('creditAccountCode', acc?.code || '');
                  handleHeaderChange('creditAccountName', acc?.name || '');
                }}>
                  <option value="">-- Default: Sales – General --</option>
                  {allAccounts.map(a => (
                    <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                  ))}
                </Select>
              </FormGroup>
              {(formData.existingPaidAmount > 0 || Number(formData.paymentAmount) > 0) && (
                <FormGroup>
                  <Label>Balance After Receipt</Label>
                  <Input value={formatAmount(totals.totalAmount - formData.existingPaidAmount - (Number(formData.paymentAmount) || 0))} disabled
                    style={{ fontWeight: 600, color: colors.error.main }} />
                </FormGroup>
              )}
            </FormGrid>
          </PaymentSection>
        </FormCard>

        <FormCard>
          <SectionTitle>Additional Information</SectionTitle>
          <FormGrid>
            <FormGroup $span2>
              <Label>Notes</Label>
              <TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Internal notes..." rows={3} />
            </FormGroup>
          </FormGrid>
        </FormCard>

        <AttachmentPanel
          transactionId={editingInvoice?.id}
          attachment={editingInvoice?.attachment}
          canEdit={canEdit}
          onChange={({ attachment }) => setEditingInvoice(prev => prev ? { ...prev, attachment } : prev)}
        />
      </>
    );
  };

  return (
    <PageLayout pageTitle="Non-Tax Sale Invoice" breadcrumb="Sales / Non-Tax Sale Invoice">
      {letterheadPickFor && (
        <LetterheadPicker
          companyId={activeCompany?.id}
          companyName={activeCompany?.companyName}
          letterheads={listInvoiceLetterheads(printConfig)}
          onClose={() => setLetterheadPickFor(null)}
          onPrint={(choice) => {
            const pick = letterheadPickFor;
            setLetterheadPickFor(null);
            handlePrint(pick.inv, letterheadForChoice(choice, listInvoiceLetterheads(printConfig)));
          }}
        />
      )}
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

const ItemThumb = styled.img`
  width: 32px;
  height: 32px;
  border-radius: ${borderRadius.sm};
  object-fit: cover;
  border: 1px solid ${colors.border.light};
`;

const ItemThumbEmpty = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${borderRadius.sm};
  border: 1px dashed ${colors.border.main};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.secondary};
  opacity: 0.4;
`;

export default SaleNonTaxInvoice;
